import { withDefaults } from "stores/utils/withDefaults";
import { createStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { IpdbField } from "api/ipdp";

import { LegendItemModel } from "models/LegendItem";
import { IpdbDisplaySettings, makeIpdbDisplayDefaults } from "models/ipdbDisplaySettings";

// ─── Legacy exported types (kept — still consumed outside this module) ───────
export interface IpdbBin {
  lessThan: number | string;
  binSize: number | string;
  greaterThan: number | string;
}

export interface SelectedIpdbFieldData {
  name: string;
  minMax: Maybe<{ min: number; max: number }>;
  ipdbBin: Maybe<IpdbBin>;
  percentiles?: Maybe<{ p10: number; p90: number }>;
}

// ─── State ───────────────────────────────────────────────────────────────────
// Flattens the 25-field `IpdbContextState` directly onto the store, preserving
// the single generic merge action the replaced Context reducer exposed.
// `showIpdb`/`reverseColor` are sourced from the shared, drift-free
// `IpdbDisplaySettings` intersection (also composed by `XDASettingsT` in
// components/vis/context/types.ts) instead of being redeclared here.
export interface IpdbState extends IpdbDisplaySettings {
  // Legend / visibility
  legendItems: LegendItemModel[];
  legendTitle: string;

  // Data selection
  modelSource: string;
  selectedIpdbFieldData: SelectedIpdbFieldData;
  selectedZoneKeys: string[];
  selectedIpdbZones: string[];
  ipdbZoneInCalculation: string[];
  searchValue: string;
  treeData: unknown[];
  orgLayerKeyToName: Map<string, string>;
  orgLayerKeyToGroup: Map<string, string>;
  expandedKeys: string[];
  useNativeSource: boolean;
  allFieldsDataForSelectedZones: IpdbField[];

  // Bin settings
  isBinUsingDefaultValues: boolean;

  // Display options
  ipdbColorPaletteName: string;
  interpolateColors: boolean;
  showControlWells: boolean;
  showZero: boolean;

  // Error messages
  binErrorMsg: string;
  dataErrorMsg: string;
  colorErrorMsg: string;

  // Loading
  isLoading: boolean;
}

// ─── Actions ─────────────────────────────────────────────────────────────────
export interface IpdbActions {
  // Single generic merge action — matches the existing
  // `dispatch({type:"update", payload})` call sites almost 1:1.
  readonly update: (patch: Partial<IpdbState>) => void;
  readonly reset: () => void;
}

// ─── Combined store type ─────────────────────────────────────────────────────
export type IpdbStore = IpdbState & IpdbActions;

// ─── Initial state factory ───────────────────────────────────────────────────
// `makeIpdbDefaults` is invoked per call (via `withDefaults`) so every store
// instance gets its own fresh `Map`/array references — required for
// scoped-store isolation.
const makeIpdbDefaults = (): IpdbState => ({
  ...makeIpdbDisplayDefaults(),
  legendItems: [],
  legendTitle: "",

  modelSource: "",
  selectedIpdbFieldData: { name: "", minMax: null, ipdbBin: null },
  selectedZoneKeys: [],
  selectedIpdbZones: [],
  ipdbZoneInCalculation: [],
  searchValue: "",
  treeData: [],
  orgLayerKeyToName: new Map(),
  orgLayerKeyToGroup: new Map(),
  expandedKeys: ["eva_research_group", "organization_group"],
  useNativeSource: false,
  allFieldsDataForSelectedZones: [],

  isBinUsingDefaultValues: true,

  ipdbColorPaletteName: "portland",
  interpolateColors: true,
  showControlWells: false,
  showZero: true,

  binErrorMsg: "",
  dataErrorMsg: "",
  colorErrorMsg: "",

  isLoading: false
});

export const createInitialState = withDefaults(makeIpdbDefaults);

// ─── Store factory ───────────────────────────────────────────────────────────
export const createIpdbStore = (overrides?: Partial<IpdbState>) => {
  const initial = createInitialState(overrides);

  return createStore<IpdbStore>()(
    subscribeWithSelector((set) => ({
      ...initial,

      update: (patch) => set(patch),

      reset: () => set(createInitialState(overrides))
    }))
  );
};

export type IpdbStoreApi = ReturnType<typeof createIpdbStore>;
